// Sentry initialization should be imported first for early error capture

import { ClerkProvider, useAuth, useUser } from "@clerk/clerk-react";
import { enUS, ptBR } from "@clerk/localizations";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { StrictMode, useEffect } from "react";
import ReactDOM from "react-dom/client";
import { Toaster } from "sonner";
import { setGlobalGetToken } from "./api/api_client.ts";
import { LoadingSpinner } from "./components/LoadingSpinner.tsx";
import { LanguageProvider, useLanguage } from "./hooks/useLanguage.tsx";
import { ThemeProvider } from "./hooks/useTheme.tsx";
import { initSentry, Sentry, setSentryUser } from "./sentry.ts";
import "./i18n/index.ts";
import type { LanguageKey } from "./i18n/index.ts";
import reportWebVitals from "./reportWebVitals.ts";
import { routeTree } from "./routeTree.gen.ts";
import "./styles.css";

const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
if (!PUBLISHABLE_KEY) {
	throw new Error("Add your Clerk Publishable Key to the .env.local file");
}

const queryClient = new QueryClient();

// Create a new router instance
const router = createRouter({
	routeTree,
	context: {
		queryClient,
		userId: undefined!,
		get_token: undefined!,
	},
	defaultPreload: false,
	scrollRestoration: true,
	defaultStructuralSharing: true,
	defaultPreloadStaleTime: 0,
	defaultOnCatch: (error) => {
		// Handle stale chunk errors after deployment - when browser tries to load
		// old JS chunks that no longer exist, server returns HTML (404 page)
		const isChunkLoadError =
			error instanceof TypeError && error.message?.includes("MIME type");

		if (isChunkLoadError) {
			// Force reload to fetch fresh chunks
			window.location.reload();
		}
	},
});

// Register the router instance for type safety
declare module "@tanstack/react-router" {
	interface Register {
		router: typeof router;
	}
}

// Initialize Sentry with router for navigation tracking
initSentry(router);

// Map language keys to Clerk localizations
const clerkLocalizationMap: Record<LanguageKey, typeof enUS | typeof ptBR> = {
	en_US: enUS,
	pt_BR: ptBR,
};

/**
 * Component to sync Clerk user context to Sentry for error attribution.
 */
function SentryUserSync() {
	const { user, isLoaded } = useUser();

	useEffect(() => {
		if (isLoaded) {
			if (user) {
				setSentryUser({
					id: user.id,
					email: user.primaryEmailAddress?.emailAddress,
					username: user.fullName || undefined,
				});
			} else {
				setSentryUser(null);
			}
		}
	}, [isLoaded, user]);

	return null;
}

function SetupQueryClient() {
	const { userId, getToken, isLoaded } = useAuth();

	// Set up global token function for API client interceptors
	useEffect(() => {
		if (isLoaded && getToken) {
			setGlobalGetToken(getToken);
		}
	}, [isLoaded, getToken]);

	if (!isLoaded) {
		return (
			<div className="min-h-screen bg-base-100 flex items-center justify-center">
				<LoadingSpinner size="lg" />
			</div>
		);
	}

	return (
		<QueryClientProvider client={queryClient}>
			<SentryUserSync />
			<RouterProvider
				router={router}
				context={{
					userId,
					get_token: getToken,
				}}
			/>
			<Toaster
				position="top-right"
				expand={true}
				richColors={true}
				closeButton={true}
				duration={4000}
				toastOptions={{
					className: "!bg-base-100 !text-base-content !border-base-300",
					style: {
						backgroundColor: "hsl(var(--b1))",
						color: "hsl(var(--bc))",
						border: "1px solid hsl(var(--b3))",
					},
				}}
			/>
		</QueryClientProvider>
	);
}

// ClerkProvider wrapper that uses dynamic localization based on selected language
function ClerkProviderWithLocalization({
	children,
}: {
	children: React.ReactNode;
}) {
	const { currentLanguage } = useLanguage();
	const clerkLocalization = clerkLocalizationMap[currentLanguage];

	return (
		<ClerkProvider
			localization={clerkLocalization}
			publishableKey={PUBLISHABLE_KEY}
			afterSignOutUrl="/"
			signInUrl="/sign-in"
			signUpUrl="/sign-up"
			signInFallbackRedirectUrl="/app/select-organization"
			signUpFallbackRedirectUrl="/app/select-organization"
		>
			{children}
		</ClerkProvider>
	);
}

const rootElement = document.getElementById("app");
if (rootElement && !rootElement.innerHTML) {
	// React 19 error hooks for automatic Sentry error capture
	const root = ReactDOM.createRoot(rootElement, {
		// Called when an error is thrown and not caught by an ErrorBoundary
		onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
			console.error("Uncaught error:", error, errorInfo.componentStack);
		}),
		// Called when React catches an error in an ErrorBoundary
		onCaughtError: Sentry.reactErrorHandler(),
		// Called when React automatically recovers from errors
		onRecoverableError: Sentry.reactErrorHandler(),
	});

	root.render(
		<StrictMode>
			<LanguageProvider>
				<ClerkProviderWithLocalization>
					<ThemeProvider>
						<SetupQueryClient />
					</ThemeProvider>
				</ClerkProviderWithLocalization>
			</LanguageProvider>
		</StrictMode>,
	);
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
